import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { GameAudioProvider } from '@/shared/hooks/useGameAudioSettings';
import { EndOutcome, GAME_TYPES } from '@/features/gameSession/contracts';
import { getElapsedSeconds } from '@/shared/utils/time';
import { useBackgroundMusic } from '../hooks/useBackgroundMusic';
import { MeasurementGameScreen, WORLD_KEYS } from '../types';
import { createWorldSequence } from '../utils/worldSequence';
import { getMeasurementLevels } from '../utils/levels';

import GameStyleBoundary from '@/shared/components/gameStyleBoundary/GameStyleBoundary';
import Spinner from '@/shared/components/spinner/Spinner';

import '../styles/measurement-game.scss';

import type { CSSProperties } from 'react';
import type {
	GameAnswerEvent,
	GameAudioProps,
	GameCloseEvent,
	GameData,
	GameFinishEvent,
} from '@/features/gameSession';
import type { MeasurementGameData, MeasurementGameLevelProgress, NpcFactUpdateHandler, NpcSession } from '../types';

export type MeasurementGameProps = GameAudioProps & {
	className?: string;
	style?: CSSProperties;
	gameData?: MeasurementGameData | null;
	levelProgress?: MeasurementGameLevelProgress | null;
	totalStars?: number;
	onUpdateUserData?: NpcFactUpdateHandler;
	autostart?: boolean;
	onStart?: () => void;
	onAnswer?: (event: GameAnswerEvent) => void;
	onFinish?: (event: GameFinishEvent) => void;
	onReplay?: (gameData: GameData) => void;
	onClose?: (event?: GameCloseEvent) => void;
};

const StartScreen = lazy(() => import('./StartScreen'));
const GameScreen = lazy(() => import('./GameScreen'));
const EndScreen = lazy(() => import('./EndScreen'));
const WorldBackground = lazy(() => import('./WorldBackground'));

const MeasurementGameContent = ({
	autostart = false,
	className,
	gameData,
	levelProgress,
	onAnswer,
	onClose,
	onFinish,
	onReplay,
	onStart,
	style,
	totalStars,
	onUpdateUserData,
}: MeasurementGameProps) => {
	const { t } = useTranslation();
	useBackgroundMusic();
	const [activeScreen, setActiveScreen] = useState(
		autostart ? MeasurementGameScreen.Game : MeasurementGameScreen.Start,
	);
	const [levelIndex, setLevelIndex] = useState(0);
	const [gameLoading, setGameLoading] = useState(true);
	const initialAmount = gameData?.game?.total_score ?? 0;
	const [totalAmount, setTotalAmount] = useState(initialAmount);
	const [outcome, setOutcome] = useState(EndOutcome.LOST);
	const questionStartedAtRef = useRef<number | null>(autostart ? Date.now() : null);
	const npcSessionRef = useRef<NpcSession>({ encounterSeen: false, factShown: false });
	const attemptNumberRef = useRef(0);
	const questions = gameData?.questions;
	const levels = useMemo(() => getMeasurementLevels({ questions }), [questions]);
	const worlds = useMemo(
		() => createWorldSequence(levels.length, gameData?.firstWorld),
		[gameData?.firstWorld, levels.length],
	);
	const level = levels[levelIndex];
	const world = level?.world ?? worlds[levelIndex] ?? WORLD_KEYS[0];
	const startWorld = levels[0]?.world ?? worlds[0] ?? WORLD_KEYS[0];
	useEffect(() => {
		if (activeScreen !== MeasurementGameScreen.Start) return;
		void import('../phaser/rendering/backgrounds')
			.then(({ WORLD_BACKGROUNDS }) => {
				const background = new Image();
				background.src = WORLD_BACKGROUNDS[startWorld];
			})
			.catch(() => undefined);
	}, [activeScreen, startWorld]);
	useEffect(() => setTotalAmount(initialAmount), [initialAmount]);
	const handleStart = useCallback(() => {
		questionStartedAtRef.current = Date.now();
		attemptNumberRef.current = 0;
		npcSessionRef.current = { encounterSeen: false, factShown: false };
		setLevelIndex(0);
		setGameLoading(true);
		setTotalAmount(initialAmount);
		setActiveScreen(MeasurementGameScreen.Game);
		onStart?.();
	}, [initialAmount, onStart]);
	const handleReplay = useCallback(() => {
		const nextGameData = levelProgress?.user_learning_path_section;
		if (nextGameData) {
			onReplay?.(nextGameData);
		}
		handleStart();
	}, [handleStart, levelProgress, onReplay]);
	const handleBack = useCallback(() => {
		if (onClose) {
			onClose();
			return;
		}
		setActiveScreen(MeasurementGameScreen.Start);
	}, [onClose]);
	const handleAnswer = useCallback(
		(answerText: string, isCorrect: boolean) => {
			const questionIndex = gameData?.questions?.findIndex((question) => question?.id === level?.id) ?? -1;
			const question = gameData?.questions?.[questionIndex];
			if (!question) return;
			attemptNumberRef.current += 1;
			onAnswer?.({
				gameType: GAME_TYPES.MEASUREMENT_GAME,
				question: {
					...question,
					ai_tutor_enabled: question.ai_tutor_enabled ?? false,
					explanation_available: question.explanation_available ?? false,
					time_estimate_in_seconds: question.time_estimate_in_seconds ?? 0,
					title: question.title ?? '',
					topic_id: question.topic_id ?? 0,
					topic_title: question.topic_title ?? '',
				},
				questionIndex,
				isCorrect,
				answerId: question.answers.find((answer) => answer?.is_correct)?.id,
				answerTexts: [answerText],
				answerTimeInSeconds: getElapsedSeconds(questionStartedAtRef.current),
				attemptNumber: attemptNumberRef.current,
				isSecondAttempt: attemptNumberRef.current > 1,
			});
		},
		[gameData, level, onAnswer],
	);
	const handleFinish = useCallback(
		(event: GameFinishEvent) => {
			if (event.gameOutcome === EndOutcome.SUCCEEDED) setTotalAmount((amount) => amount + 5);
			if (event.gameOutcome === EndOutcome.SUCCEEDED && levelIndex < levels.length - 1) {
				questionStartedAtRef.current = Date.now();
				attemptNumberRef.current = 0;
				setLevelIndex(levelIndex + 1);
				setGameLoading(true);
				return;
			}
			setActiveScreen(MeasurementGameScreen.End);
			setOutcome(event.gameOutcome);
			onFinish?.(event);
		},
		[levelIndex, levels.length, onFinish],
	);

	let screen = <StartScreen onStart={handleStart} onBack={handleBack} totalAmount={totalAmount} />;

	if (activeScreen === MeasurementGameScreen.Game) {
		screen = (
			<GameScreen
				npcSessionRef={npcSessionRef}
				totalStars={totalStars ?? totalAmount}
				onUpdateUserData={onUpdateUserData}
				onLoadingChange={setGameLoading}
				introMessages={levelIndex === 0 ? gameData?.game?.messages?.intro ?? undefined : undefined}
				level={level}
				totalAmount={totalAmount}
				world={world}
				onAnswer={handleAnswer}
				onFinish={handleFinish}
				onBack={handleBack}
				onCoinCollect={(value) => setTotalAmount((amount) => amount + value)}
			/>
		);
	} else if (activeScreen === MeasurementGameScreen.End) {
		screen = (
			<EndScreen
				outcome={outcome}
				totalAmount={totalAmount}
				levelProgress={levelProgress}
				onReplay={handleReplay}
				onClose={handleBack}
			/>
		);
	}

	const isGameplay = activeScreen === MeasurementGameScreen.Game;
	const loadingScreen = (
		<div className={classNames('measurement-game__loading', { 'measurement-game__loading--world': isGameplay })}>
			{isGameplay && (
				<Suspense fallback={null}>
					<WorldBackground world={world} className="measurement-game__loading-background" />
				</Suspense>
			)}
			<div
				className="measurement-game__loading-indicator"
				role="status"
				aria-label={t('measurement_game.game.loading')}
			>
				<Spinner classname="measurement-game__loading-spinner" />
			</div>
		</div>
	);

	return (
		<GameStyleBoundary>
			<div className={classNames('measurement-game', className)} style={style} data-screen={activeScreen}>
				{isGameplay && gameLoading && loadingScreen}
				<Suspense fallback={isGameplay ? null : loadingScreen}>{screen}</Suspense>
			</div>
		</GameStyleBoundary>
	);
};

const MeasurementGame = ({ audioOn, onAudioChange, ...props }: MeasurementGameProps) => (
	<GameAudioProvider audioOn={audioOn} onAudioChange={onAudioChange}>
		<MeasurementGameContent {...props} />
	</GameAudioProvider>
);

export default MeasurementGame;
